import React, { useState } from 'react';
import { FaRegStar } from 'react-icons/fa';
import classNames from 'styles/utils/classNames';
import {
  formatEPS,
  formatMarketCap,
  formatPercentage,
  formatNumber,
  calculateSurprisePercentage,
  formatCalendarDay,
} from 'screens/Calendar';
import { TickerType } from '../../types';

interface EarningsCardProps {
  item: any;
  isBookmarked: boolean;
  onBookmark: (symbol: string) => void;
  tickerData: TickerType[];
  slug?: string;
}

export const EarningsCard: React.FC<EarningsCardProps> = ({
  item,
  isBookmarked,
  onBookmark,
  tickerData,
}) => {
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  const surprise =
    item.epsActual != null && item.epsEstimated != null ? item.epsActual - item.epsEstimated : null;

  const surprisePct = calculateSurprisePercentage(item.epsActual, item.epsEstimated);
  const foundTicker = tickerData.find((ticker) => ticker.t === item.symbol);
  const dayOfWeek = item.date ? formatCalendarDay(item.date, { weekday: 'long' }) : null;

  return (
    <>
      <div className="rounded-lg border border-palette-border bg-palette-black-2 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-start gap-2">
            <button
              onClick={() => onBookmark(item.symbol)}
              className={classNames(
                'p-1 transition-all duration-200',
                isBookmarked ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400',
              )}
            >
              <FaRegStar className="h-4 w-4" />
            </button>
            {imageStatus === 'error' ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-xs font-semibold text-gray-600">
                {item.symbol?.[0]?.toUpperCase() || '•'}
              </div>
            ) : (
              <img
                src={`https://images.financialmodelingprep.com/symbol/${item.symbol}.png`}
                alt={`${item.symbol} logo`}
                className={classNames(
                  'h-8 w-8 rounded-lg transition-opacity',
                  imageStatus === 'loading' && 'opacity-50',
                )}
                loading="lazy"
                onLoad={() => setImageStatus('loaded')}
                onError={() => setImageStatus('error')}
              />
            )}
            <div>
              <div className="text-sm font-semibold text-white">{item.symbol}</div>
              {foundTicker?.n && (
                <div className="text-xs text-gray-400">{foundTicker?.n || item.symbol}</div>
              )}
              {item.marketCap && (
                <div className="text-xs text-gray-400">
                  {formatMarketCap(item.marketCap)} Market Cap
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400">Earnings Date</div>
            <div className="text-xs text-white">{item.date}</div>
            {dayOfWeek && <div className="text-xs text-gray-400">{dayOfWeek}</div>}
          </div>
        </div>
        {(item.epsEstimated || item.epsActual) && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="block text-xs text-gray-400">Forecasted EPS</span>
                <span>{formatEPS(item.epsEstimated)}</span>
              </div>
              <div className="text-right">
                <span className="block text-xs text-gray-400">Reported EPS</span>
                <span>{formatEPS(item.epsActual)}</span>
              </div>
            </div>
            {(surprise !== null || surprisePct !== null) && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                {surprise !== null && (
                  <div>
                    <span className="block text-xs text-gray-400">Surprise</span>
                    <span
                      className={classNames(
                        surprise > 0 ? 'text-brand-green-primary' : 'text-red-500',
                      )}
                    >
                      {formatNumber(surprise, 2)}
                    </span>
                  </div>
                )}
                {surprisePct !== null && (
                  <div className="text-right">
                    <span className="block text-xs text-gray-400">Surprise %</span>
                    <span
                      className={classNames(
                        surprisePct > 0 ? 'text-brand-green-primary' : 'text-red-500',
                      )}
                    >
                      {formatPercentage(surprisePct, 1)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {(item.revenueEstimated || item.revenueActual) && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="block text-xs text-gray-400">Forecasted Revenue</span>
                <span>{formatMarketCap(item.revenueEstimated)}</span>
              </div>
              <div className="text-right">
                <span className="block text-xs text-gray-400">Reported Revenue</span>
                <span>{formatMarketCap(item.revenueActual)}</span>
              </div>
            </div>
          </div>
        )}
        {(item.priceTargetTrend !== '--' || item.consensus !== '--') && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="block text-xs text-gray-400">Price Target Trend</span>
                <span
                  className={classNames(
                    item.priceTargetTrend === '--' ||
                      item.priceTargetTrend === '-' ||
                      item.priceTargetTrend === null ||
                      item.priceTargetTrend === undefined
                      ? 'text-gray-400'
                      : (() => {
                          const trendValue =
                            typeof item.priceTargetTrend === 'string'
                              ? parseFloat(item.priceTargetTrend)
                              : item.priceTargetTrend;
                          return isNaN(trendValue)
                            ? 'text-gray-400'
                            : trendValue > 0
                            ? 'text-green-500'
                            : 'text-red-500';
                        })(),
                  )}
                >
                  {(() => {
                    const trend = item.priceTargetTrend;
                    if (trend === '-' || trend === '--' || trend === null || trend === undefined) {
                      return '-';
                    }
                    const trendValue = typeof trend === 'string' ? parseFloat(trend) : trend;
                    if (isNaN(trendValue)) {
                      return '-';
                    }
                    return formatPercentage(trendValue);
                  })()}
                </span>
              </div>

              <div className="text-right">
                <span className="block text-xs text-gray-400">Analyst Rating</span>
                <span
                  className={classNames(
                    item.consensus === 'Strong Buy' || item.consensus === 'Buy'
                      ? 'text-brand-green-primary'
                      : item.consensus === 'Hold'
                      ? 'text-yellow-500'
                      : item.consensus === 'Strong Sell' || item.consensus === 'Sell'
                      ? 'text-red-500'
                      : 'text-gray-400',
                  )}
                >
                  {item.consensus ?? '--'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
